<template>
  <div class="reference-currency-view">
    <div class="reference-currency-header">
      <ExBreadcrumbs :breadcrumbsList="breadcrumbItems" />
    </div>
    <OperatingCurrenciesTab
      :currencies="currencyList"
      :data-source="currencyList"
      :server-side-pagination="true"
      :external-total="currencyListTotal"
      :total-count="currencyListTotal"
      :synced-search-query="currencySearchQuery"
      :dropdown-currency-list="dropdownCurrencyList"
      :operating-catalog-currency-ids="operatingCatalogCurrencyIds"
      :add-error-code="addErrorCode"
      :store-persist-filters="true"
      store-filters-key-prefix="currency-filter-"
      @onAddCurrency="(...args) => emit('onAddCurrency', ...args)"
      @add-currency-confirm="(...args) => emit('onAddCurrency', ...args)"
      @reloadData="$emit('reloadData')"
      @currency-page-change="$emit('currency-page-change', $event)"
      @currency-search="$emit('currency-search', $event)"
      @currency-status-filter="$emit('currency-status-filter', $event)"
      @ensure-currency-catalog="$emit('ensure-currency-catalog')"
      @set-rate="$emit('set-rate', $event)"
      @rate-mode-change="$emit('rate-mode-change', $event)"
      @menu-action="$emit('menu-action', $event)"
      @bulk-set-mode="$emit('bulk-set-mode', $event)"
      @get-chatter-list="getCurrencyChatterList($event, 1)"
      :chatter-list="chatterList"
      :total-checkpoint-chatter="totalCurrencyChatter"
      @requestMoreChatter="loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { getChatterList } from "@/api";
import OperatingCurrenciesTab from "@components/OperatingCurrenciesTab/OperatingCurrenciesTab.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";

defineProps({
  currencyList: {
    required: false,
    type: Array,
    default() {
      return [];
    },
  },
  dropdownCurrencyList: {
    required: false,
    type: Array,
    default() {
      return [];
    },
  },
  operatingCatalogCurrencyIds: {
    type: Array,
    default() {
      return [];
    },
  },
  currencyListTotal: {
    type: Number,
    default: 0,
  },
  currencySearchQuery: {
    type: String,
    default: "",
  },
  addErrorCode: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "onAddCurrency",
  "reloadData",
  "currency-page-change",
  "currency-search",
  "currency-status-filter",
  "ensure-currency-catalog",
  "set-rate",
  "rate-mode-change",
  "menu-action",
  "bulk-set-mode",
]);

const breadcrumbItems = [
  { text: "Configuration", href: "/settings/configuration", disabled: false },
  { text: "Currencies", disabled: true },
];

const chatterList = ref([]);
const totalCurrencyChatter = ref(0);
const currentCurrencyData = ref(null);
const isLoadingChatter = ref(false);

const chatterPage = computed(() => {
  return Math.ceil(chatterList.value.length / 10) || 0;
});

const getCurrencyChatterList = async (item, page) => {
  try {
    isLoadingChatter.value = true;
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    if (page === 1) {
      currentCurrencyData.value = item;
      chatterList.value = [];
    }

    const { data } = await getChatterList(accountId, item.id, "operatingcurrency", page);

    if (data && data.count !== undefined) {
      totalCurrencyChatter.value = data.count;
    }

    if (data && data.results && Array.isArray(data.results)) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");
        const newEl = {};
        newEl.id = el.id;

        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }

        newEl.IsExIQtiveBot = false;
        newEl.message = truncateHtml(el.note);
        newEl.created_on = millisecondsString;
        newEl.IsHistory = el.is_history;
        return newEl;
      });

      chatterList.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching chatter list:", error);
  } finally {
    isLoadingChatter.value = false;
  }
};

const loadMoreChatter = async () => {
  if (isLoadingChatter.value) {
    return;
  }

  if (chatterList.value.length >= totalCurrencyChatter.value) {
    return;
  }

  if (currentCurrencyData.value) {
    const nextPage = chatterPage.value + 1;
    await getCurrencyChatterList(currentCurrencyData.value, nextPage);
  }
};

const truncateHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};
</script>

<style scoped>
.reference-currency-view {
  margin: 0px 8px 0px 8px;
}
.reference-currency-header {
  margin: 0px 0px 8px 0px;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
